import axios from 'axios'
import { isLocal, isProduction, isStg } from 'modules/app-config/environment'
import { STG_ENV } from 'modules/app-config/stg-env'

const LOCAL_PBG_URL = 'http://localhost:3001'

const pbgClient = axios.create({
  baseURL: getUrl(),
  headers: { 'Content-Type': 'application/json' },
  timeout: 5 * 60 * 1000, // 5 minutes
})

pbgClient.interceptors.request.use(
  (config) => {
    const { idToken } = JSON.parse(localStorage.getItem('admin-token') || '{}')
    if (idToken) {
      config.headers.Authorization = idToken
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

export function getUrl() {
  if (isStg()) {
    return STG_ENV.partnerBenefitGraphUrl
  }
  if (isProduction()) {
    return 'https://partner-benefit-graph.docday.com'
  }
  return 'https://partner-benefit-graph.dev.surecolabs.com'
}

/** Noyo Issues hits a local graph; Benefit Integrations and other PBG calls stay on remote DEV. */
export function getNoyoIssuesUrl() {
  if (isLocal()) {
    return LOCAL_PBG_URL
  }
  return getUrl()
}

export enum PartnerEnum {
  NOYO = 'Noyo',
  Ideon = 'Ideon',
  OSCAR = 'Oscar',
  Kaiser = 'Kaiser Permanente',
  HealthSherpa = 'Health Sherpa',
  UNKNOWN = 'Unknown',
}

// API functions for partner benefit graph
export const submitHealthBenefitApplication = async (
  benefitPublicKey: string,
  partner: PartnerEnum
): Promise<number> => {
  const response = await pbgClient.post(
    `/api/v1/health-benefit/${benefitPublicKey}/sync`,
    {
      partner,
    }
  )
  return response.data
}

/** PBG sets `canSubmit` from `blockedReason === null`, so the two always agree. */
export type HealthPartnerIntegrationItem = {
  displayName: string
  partnerType: PartnerEnum
  canSubmit: boolean
  /** A sentence written for an ops admin, shown as is. */
  blockedReason: string | null
}

export type PartnerBenefitEventItem = {
  id: number
  eventType?: string
  eventData?: Record<string, unknown>
  createdBy?: string
  createdTimestamp: string
}

export type HealthBenefitSyncStatusResponse = {
  task?: {
    id: number
    partnerBenefitName: string
    partnerSyncStatus: string
    taskStatus: string
    syncRequestReason?: string
    requestInitiatedBy?: string
    createdTimestamp: string
    updatedTimestamp?: string
    partnerBenefitEvent?: PartnerBenefitEventItem[]
  } | null
  integrationSettings?: {
    /** Empty when no partner integrates with the application's carrier. */
    partners: HealthPartnerIntegrationItem[]
  }
}

// App Detail's submission section stays on "Loading…" until this settles: a hung PBG should reach
// Retry fast, not after the client's 5-minute default.
const SUBMISSION_STATUS_TIMEOUT_MS = 15_000

export const getHealthBenefitPartnerSubmissionStatus = async (
  benefitPublicKey: string
): Promise<HealthBenefitSyncStatusResponse> => {
  const response = await pbgClient.get(
    `/api/v1/health-benefit/${benefitPublicKey}/sync/status`,
    { timeout: SUBMISSION_STATUS_TIMEOUT_MS }
  )
  return response.data
}

interface SyncToNoyoRequest {
  employeePublicKey: string
  employmentStatus?: string | null
  requestInitiatedBy?: string
}

/** Terminated employment (including "Terminated No Cobra") resyncs as a termination. */
export function getNoyoSyncEvent(employmentStatus?: string | null) {
  if ((employmentStatus ?? '').toLowerCase().includes('terminated')) {
    return 'EMPLOYEE_TERMINATED'
  }
  return 'CART_COMMITTED'
}

interface SyncToNoyoResponse {
  id: number
  employeePublicKey: string
  clientPublicKey: string
  enrollmentPublicKey: string
  taskStatus: string
  partnerBenefitName: string
  syncRequestReason: string
  createdTimestamp: string
}

/**
 * Creates a manual sync task with Noyo for an employee
 */
export const syncToNoyo = async (
  request: SyncToNoyoRequest
): Promise<SyncToNoyoResponse> => {
  const response = await pbgClient.post('/api/v1/noyo/tasks', {
    employeePublicKey: request.employeePublicKey,
    event: getNoyoSyncEvent(request.employmentStatus),
    partner: PartnerEnum.NOYO,
    requestInitiatedBy:
      request.requestInitiatedBy ?? 'Admin:resync-noyo-application',
  })
  return response.data
}

export default pbgClient
